import { LocalDate } from "@js-joda/core";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { TextArea } from "components/designV2/textarea/textarea";
import {
  CompanyCustomer,
  CompanyCustomerProduct,
  CustomerProductType,
} from "model/Company";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomerSection,
  CustomerSectionTitle,
  ServiceFormActions,
  ServiceFormCheckboxRow,
  ServiceFormContent,
  ServiceFormDateGrid,
  ServiceFormField,
  ServiceFormGrid,
} from "./customerPanel.styled";

type Props = {
  customer: CompanyCustomer | null;
  isSaving: boolean;
  setShowAddCustomerProductForm: React.Dispatch<React.SetStateAction<boolean>>;
  showAddCustomerServiceForm: boolean;
  saveProduct(product: CompanyCustomerProduct): Promise<boolean>;
};

const toDateStart = (date: LocalDate): Date =>
  new Date(Date.UTC(date.year(), date.monthValue() - 1, date.dayOfMonth(), 3));

const toDateEnd = (date: LocalDate): Date =>
  new Date(
    Date.UTC(date.year(), date.monthValue() - 1, date.dayOfMonth(), 23, 59),
  );

export const AddCustomerServiceForm: React.FC<Props> = ({
  setShowAddCustomerProductForm,
  showAddCustomerServiceForm,
  isSaving,
  saveProduct,
  customer,
}) => {
  const { t } = useTranslation();
  const [price, setPrice] = useState("");
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [startDate, setStartDate] = useState<LocalDate | null>(null);
  const [endDate, setEndDate] = useState<LocalDate | null>(null);
  const [datesShouldBeSet, setDatesShouldBeSet] = useState(false);

  const agreementId = customer?.agreements?.[0]?.id;
  const withMandatory = (label: string): string =>
    `${label} (${t("Staff.AddStaffPanelMandatory")})`;

  const isSaveDisabled = useMemo(
    (): boolean =>
      !title.trim() ||
      !price.trim() ||
      !agreementId ||
      (datesShouldBeSet && (!startDate || !endDate)),
    [agreementId, datesShouldBeSet, endDate, price, startDate, title],
  );

  const onClose = (): void => {
    setShowAddCustomerProductForm(false);
  };

  const handleSave = async (): Promise<void> => {
    if (!agreementId) return;

    const product: CompanyCustomerProduct = {
      agreementId,
      title,
      price: Number(price),
      overtimeRate: 0,
      productType: CustomerProductType.HOURLY,
      comment,
    };

    if (datesShouldBeSet && startDate && endDate) {
      product.startDate = toDateStart(startDate);
      product.endDate = toDateEnd(endDate);
    }

    const wasSaved = await saveProduct(product);

    if (wasSaved) {
      setShowAddCustomerProductForm(false);
    }
  };

  return (
    <Modal
      isOpen={showAddCustomerServiceForm}
      onClose={onClose}
      label={t("CompanyCustomers.AddProduct")}
      size="medium"
      footer={
        <ServiceFormActions>
          <SecondaryButton
            label={t("General.Close")}
            onClick={onClose}
            size="small"
            type="button"
          />
          <PrimaryButton
            label={t("General.Save")}
            onClick={() => {
              void handleSave();
            }}
            disabled={isSaveDisabled || isSaving}
            isLoading={isSaving}
            size="small"
            type="button"
          />
        </ServiceFormActions>
      }
    >
      <ServiceFormContent>
        <CustomerSection>
          <CustomerSectionTitle>
            {t("CompanyCustomers.AddProduct")}
          </CustomerSectionTitle>
          <ServiceFormGrid>
            <ServiceFormField>
              <InputField
                label={withMandatory(t("CompanyCustomers.Title"))}
                value={title}
                onChange={setTitle}
                fullWidth
              />
            </ServiceFormField>

            <ServiceFormField>
              <InputField
                label={withMandatory(t("CompanyCustomers.Price"))}
                value={price}
                onChange={setPrice}
                type="number"
                fullWidth
              />
            </ServiceFormField>
          </ServiceFormGrid>

          <ServiceFormCheckboxRow>
            <Checkbox
              label={t("CompanyCustomers.SetDateForProduct")}
              checked={datesShouldBeSet ? "checked" : "unchecked"}
              onChange={() =>
                setDatesShouldBeSet((currentValue) => !currentValue)
              }
            />
          </ServiceFormCheckboxRow>

          {datesShouldBeSet && (
            <ServiceFormDateGrid>
              <ServiceFormField>
                <DatePicker
                  label={withMandatory(t("CompanyCustomers.StartDate"))}
                  value={startDate}
                  onChange={setStartDate}
                  dateFormat="YYYY-MM-DD"
                />
              </ServiceFormField>

              <ServiceFormField>
                <DatePicker
                  label={withMandatory(t("CompanyCustomers.EndDate"))}
                  value={endDate}
                  onChange={setEndDate}
                  dateFormat="YYYY-MM-DD"
                />
              </ServiceFormField>
            </ServiceFormDateGrid>
          )}

          <ServiceFormField>
            <TextArea
              label={t("CompanyCustomers.Comment")}
              value={comment}
              onChange={setComment}
              minHeight={120}
              maxHeight={220}
            />
          </ServiceFormField>
        </CustomerSection>
      </ServiceFormContent>
    </Modal>
  );
};
